'use client';

import * as React from 'react';
import { Select as BaseSelect } from '@base-ui/react/select';
import styles from './Select.module.css';
import clsx from 'clsx';

export interface SelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface SelectProps {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  label?: string;
  helperText?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  className?: string;
}

export const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      options,
      placeholder = 'Select an option',
      label,
      helperText,
      error,
      disabled = false,
      required = false,
      name,
      className,
    },
    ref
  ) => {
    return (
      <div className={clsx(styles.container, className)}>
        {label && (
          <label className={styles.label}>
            {label}
            {required && <span className={styles.required}>*</span>}
          </label>
        )}
        <BaseSelect.Root
          value={value}
          defaultValue={defaultValue}
          onValueChange={onChange}
          disabled={disabled}
          name={name}
        >
          <BaseSelect.Trigger
            ref={ref}
            className={clsx(styles.trigger, error && styles.error)}
          >
            <BaseSelect.Value placeholder={placeholder}>
              {(selectedValue) => {
                const selectedOption = options.find((option) => option.value === selectedValue);
                return selectedOption ? (
                  <span className={styles.valueContent}>
                    {selectedOption.icon}
                    <span>{selectedOption.label}</span>
                  </span>
                ) : placeholder;
              }}
            </BaseSelect.Value>
            <BaseSelect.Icon className={styles.icon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </BaseSelect.Icon>
          </BaseSelect.Trigger>
          <BaseSelect.Portal>
            <BaseSelect.Positioner
              className={styles.positioner}
              side="bottom"
              sideOffset={4}
              align="start"
            >
              <BaseSelect.Popup className={styles.popup}>
                {options.map((option) => (
                  <BaseSelect.Item
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    className={styles.option}
                  >
                    <span className={styles.optionContent}>
                      {option.icon}
                      <BaseSelect.ItemText>{option.label}</BaseSelect.ItemText>
                    </span>
                    <BaseSelect.ItemIndicator className={styles.optionIndicator}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12l5 5L20 7" />
                      </svg>
                    </BaseSelect.ItemIndicator>
                  </BaseSelect.Item>
                ))}
              </BaseSelect.Popup>
            </BaseSelect.Positioner>
          </BaseSelect.Portal>
        </BaseSelect.Root>
        {helperText && !error && <span className={styles.helperText}>{helperText}</span>}
        {error && <span className={styles.errorText}>{error}</span>}
      </div>
    );
  }
);

Select.displayName = 'Select';
